/**
 * Cash custody on Cash & Bank: who holds the cash, handing it over, and
 * confirming it arrived.
 *
 * A handover moves cash from one person's hands to another's — a worker's
 * takings to the accountant. It is never revenue and never the bank: it
 * leaves the worker at once and lands with the receiver only when THEY
 * confirm it. A deposit into the bank is confirmed by someone who reconciles
 * cash, under the reference the bank gives it, so it can be found on the
 * statement. Either can be declined or withdrawn with a reason while waiting.
 */

import { useState, type ReactNode } from 'react';
import type { CashBankDashboard, MoneyCustody, MoneyTransferView } from '@cenox/shared';
import { Badge, Button, Card, ErrorState, Input, Modal, Select, TableSkeleton } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { newSubmissionKey } from '../loans/data';
import { useCompleteTransfer, useMyCustody, useRecordTransfer, useVoidTransfer } from './data';

export const TRANSFER_KIND_LABEL: Record<MoneyTransferView['kind'], string> = {
  handover: 'Handover',
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  transfer: 'Transfer',
};

/** Confirm (with the bank's reference when it lands in a bank) or decline a pending one. */
export function ConfirmOrDecline({
  transfer,
  today,
  toBank,
  mayCancel,
}: {
  transfer: MoneyTransferView;
  today: string;
  /** Whether it lands in a bank or wallet: then the bank's reference is asked for. */
  toBank: boolean;
  mayCancel: boolean;
}): ReactNode {
  const complete = useCompleteTransfer();
  const cancel = useVoidTransfer();
  const [mode, setMode] = useState<'idle' | 'confirm' | 'decline'>('idle');
  const [arrivedOn, setArrivedOn] = useState(today);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  if (mode === 'idle') {
    return (
      <div className="flex flex-wrap gap-1">
        {transfer.mayConfirm ? (
          <Button variant="secondary" size="sm" onClick={() => setMode('confirm')}>
            {transfer.kind === 'handover' ? 'I received it' : 'Confirm arrived'}
          </Button>
        ) : null}
        {mayCancel ? (
          <Button variant="ghost" size="sm" onClick={() => setMode('decline')}>
            {transfer.mayConfirm ? 'Not received' : 'Cancel'}
          </Button>
        ) : null}
      </div>
    );
  }
  if (mode === 'confirm') {
    return (
      <form
        className="mt-2 flex w-full flex-wrap items-end gap-2"
        aria-label="Confirm arrived"
        onSubmit={(e) => {
          e.preventDefault();
          complete.mutate(
            { id: transfer.id, completedOn: arrivedOn, ...(toBank ? { reference: reference.trim() } : {}) },
            { onSuccess: () => setMode('idle') },
          );
        }}
      >
        <Input label="Arrived on" type="date" min={transfer.transferredOn} max={today} value={arrivedOn} onChange={(e) => setArrivedOn(e.target.value)} />
        {toBank ? (
          <Input
            label="Bank reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Teller number or statement line"
            hint="As it appears on the bank statement"
          />
        ) : null}
        <Button type="button" variant="secondary" size="sm" onClick={() => setMode('idle')}>Back</Button>
        <Button type="submit" size="sm" loading={complete.isPending} disabled={toBank && reference.trim().length === 0}>
          {transfer.kind === 'handover' ? 'I received it' : 'Confirm it arrived'}
        </Button>
        {complete.error ? <p role="alert" className="w-full text-sm text-red-700">{complete.error.message}</p> : null}
      </form>
    );
  }
  return (
    <form
      className="mt-2 flex w-full flex-wrap items-end gap-2"
      aria-label="Not received"
      onSubmit={(e) => {
        e.preventDefault();
        cancel.mutate({ id: transfer.id, reason }, { onSuccess: () => setMode('idle') });
      }}
    >
      <div className="min-w-[12rem] flex-1">
        <Input label="Why?" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Never reached me / entered twice" />
      </div>
      <Button type="button" variant="secondary" size="sm" onClick={() => setMode('idle')}>Back</Button>
      <Button type="submit" variant="danger" size="sm" loading={cancel.isPending} disabled={reason.trim().length < 3}>Cancel it</Button>
      {cancel.error ? <p role="alert" className="w-full text-sm text-red-700">{cancel.error.message}</p> : null}
    </form>
  );
}

/** Handovers and deposits still waiting on their receiver. */
export function AwaitingConfirmation({
  transfers,
  currency,
  today,
}: {
  transfers: MoneyTransferView[];
  currency: string;
  today: string;
}): ReactNode {
  if (transfers.length === 0) return null;
  return (
    <Card>
      <h2 className="text-sm font-semibold text-slate-900">Waiting for confirmation</h2>
      <p className="mt-0.5 text-xs text-slate-500">
        Out of the sender&apos;s hands, not yet in the receiver&apos;s. Cash handed to a person is confirmed by that person;
        a deposit is confirmed under the bank&apos;s reference.
      </p>
      <ul className="mt-2 divide-y divide-slate-100" data-testid="awaiting-confirmation">
        {transfers.map((t) => (
          <li key={t.id} className="flex flex-wrap items-start justify-between gap-2 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium">
                <span className="whitespace-nowrap">{money(t.amount, currency)}</span> · {TRANSFER_KIND_LABEL[t.kind]} ·{' '}
                {t.fromAccountName} → {t.toAccountName} <Badge tone="warning">Waiting</Badge>
              </p>
              <p className="text-xs text-slate-500">
                Sent {t.transferredOn}
                {t.submittedBy ? ` by ${t.submittedBy}` : ''}
                {t.receiverName ? ` · for ${t.receiverName} to confirm` : ' · for the accountant or owner to confirm at the bank'}
                {t.reference ? ` · ref ${t.reference}` : ''}
              </p>
            </div>
            <ConfirmOrDecline transfer={t} today={today} toBank={t.kind === 'deposit' || t.kind === 'transfer'} mayCancel={t.mayCancel} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Physical cash by location, and whose hands it is in. */
export function CashByLocation({ data }: { data: CashBankDashboard }): ReactNode {
  const m = (v: string) => money(v, data.currencyCode);
  if (data.cashByLocation.length === 0) return null;
  return (
    <Card>
      <h2 className="text-sm font-semibold text-slate-900">Cash by location</h2>
      <p className="mt-0.5 text-xs text-slate-500">As of {data.asOf}. Handed over and not yet confirmed is still counted where it left.</p>
      <div className="-mx-3 mt-2 overflow-x-auto px-3">
        <table className="w-full min-w-[34rem] text-sm" data-testid="cash-by-location">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="py-1 pr-3 font-medium">Location</th>
              <th className="py-1 pr-3 font-medium">Held by</th>
              <th className="py-1 pr-3 text-right font-medium">In hand</th>
              <th className="py-1 pr-3 text-right font-medium">Not in an account</th>
              <th className="py-1 pr-3 text-right font-medium">Sent, waiting</th>
              <th className="py-1 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.cashByLocation.map((c) => (
              <tr key={c.branchId ?? 'none'}>
                <td className="py-1.5 pr-3 font-medium text-slate-900">{c.branchName ?? 'Whole business'}</td>
                <td className="py-1.5 pr-3 text-slate-600">
                  {c.holders.length === 0
                    ? '—'
                    : c.holders.map((h) => `${h.holderName ?? h.accountName}: ${m(h.balance)}`).join(' · ')}
                </td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{m(c.inAccounts)}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-amber-800">{c.unassigned === '0.00' ? '—' : m(c.unassigned)}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-amber-800">
                  {c.sentAwaitingConfirmation === '0.00' ? '—' : m(c.sentAwaitingConfirmation)}
                </td>
                <td className="whitespace-nowrap py-1.5 text-right font-semibold tabular-nums">{m(c.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Money received in the period, by method, location and the worker who recorded it. */
export function Collections({ data }: { data: CashBankDashboard }): ReactNode {
  const m = (v: string) => money(v, data.currencyCode);
  const c = data.collections;
  const [by, setBy] = useState<'byMethod' | 'byLocation' | 'byWorker' | 'bySource'>('byMethod');
  return (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Collected {data.from} to {data.to}</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Money received from customers, riders and borrowers. Handovers and deposits move it; they never add to it.
          </p>
        </div>
        <Select
          label="By"
          value={by}
          onChange={(e) => setBy(e.target.value as typeof by)}
          options={[
            { value: 'byMethod', label: 'Payment method' },
            { value: 'byLocation', label: 'Location' },
            { value: 'byWorker', label: 'Recorded by' },
            { value: 'bySource', label: 'Ledger' },
          ]}
        />
      </div>
      <p className="mt-2 text-lg font-semibold tabular-nums" data-testid="collections-total">{m(c.total)}</p>
      <dl className="mt-1 space-y-1 text-sm" data-testid="collections">
        {c[by].map((g) => (
          <div key={g.key || 'none'} className="flex justify-between gap-3">
            <dt className="capitalize text-slate-600">{g.label} <span className="text-xs text-slate-400">· {g.count}</span></dt>
            <dd className="whitespace-nowrap tabular-nums">{m(g.amount)}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

/** What the money is: owed out of it, owed to the company and not in hand. */
export function Position({ data }: { data: CashBankDashboard }): ReactNode {
  const p = data.position;
  if (!p) return null;
  const m = (v: string) => money(v, data.currencyCode);
  const row = (label: string, value: string, tone?: string) => (
    <div className="flex justify-between gap-3">
      <dt className={tone ?? 'text-slate-600'}>{label}</dt>
      <dd className={`whitespace-nowrap tabular-nums ${tone ?? ''}`}>{value}</dd>
    </div>
  );
  return (
    <Card>
      <h2 className="text-sm font-semibold text-slate-900">What this money is</h2>
      <p className="mt-0.5 text-xs text-slate-500">The whole business, as of {data.asOf}. Nothing here is booked; each figure comes from its own ledger.</p>
      <dl className="mt-2 space-y-1 text-sm" data-testid="cash-bank-position">
        {row('Held in cash and bank (incl. not in an account)', m(p.held))}
        {p.inTransit !== '0.00' ? row('On its way between accounts', m(p.inTransit)) : null}
        {row('Less: collected for investors, not yet paid', `− ${m(p.investorPayable)}`, 'text-rose-700')}
        {row('Less: borrowed, still owed', `− ${m(p.loansPayable.principal)}`, 'text-rose-700')}
        {row('Less: expenses due, not yet paid', `− ${m(p.unpaidExpensesDue)}`, 'text-rose-700')}
        <div className="flex justify-between gap-3 border-t border-slate-100 pt-1 font-semibold">
          <dt>The company&apos;s own</dt>
          <dd className="whitespace-nowrap tabular-nums">{m(p.companyOwn)}</dd>
        </div>
        <p className="pt-2 text-xs font-medium uppercase tracking-wide text-slate-500">Owed to the company, not in hand</p>
        {row(`Lent out, still owed (${p.loansReceivable.count})`, m(p.loansReceivable.principal))}
        {row('Fares riders are confirmed to hold', m(p.riderHeld))}
      </dl>
    </Card>
  );
}

/**
 * A worker's own cash: what they hold, handing it to the accountant or to the
 * bank, and handovers waiting for them to confirm.
 */
export function MyCash({ standalone }: { standalone: boolean }): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'UTC');
  const custody = useMyCustody();
  const [handing, setHanding] = useState(false);
  const data = custody.data;
  if (custody.isError) return <ErrorState message="Could not load your cash." onRetry={() => void custody.refetch()} />;
  if (!data) return standalone ? <TableSkeleton rows={2} columns={3} /> : null;
  const waitingOnMe = data.transfers.filter((t) => t.mayConfirm);
  if (!standalone && data.held.length === 0 && waitingOnMe.length === 0) return null;
  const m = (v: string) => money(v, data.currencyCode);
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Your cash</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Cash in your hands. Hand it to the accountant or bank it: it leaves you now and is theirs once they confirm it.
          </p>
        </div>
        {data.held.length > 0 && data.destinations.length > 0 ? (
          <Button size="sm" onClick={() => setHanding(true)}>Hand over cash</Button>
        ) : null}
      </div>
      {data.held.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">You do not hold a cash account.</p>
      ) : (
        <dl className="mt-2 space-y-1 text-sm" data-testid="my-cash">
          {data.held.map((h) => (
            <div key={h.accountId} className="flex justify-between gap-3">
              <dt>{h.accountName}{h.branchName ? <span className="text-slate-400"> · {h.branchName}</span> : null}</dt>
              <dd className="whitespace-nowrap font-semibold tabular-nums">{m(h.balance)}</dd>
            </div>
          ))}
        </dl>
      )}
      {data.transfers.length > 0 ? (
        <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-100" data-testid="my-transfers">
          {data.transfers.map((t) => (
            <li key={t.id} className="flex flex-wrap items-start justify-between gap-2 py-2">
              <div className="text-sm">
                <span className="whitespace-nowrap font-medium">{m(t.amount)}</span> · {t.fromAccountName} → {t.toAccountName}{' '}
                <Badge tone="warning">{t.mayConfirm ? 'Confirm you received it' : 'Waiting for the receiver'}</Badge>
                <p className="text-xs text-slate-500">Sent {t.transferredOn}{t.submittedBy ? ` by ${t.submittedBy}` : ''}</p>
              </div>
              <ConfirmOrDecline transfer={t} today={today} toBank={t.kind === 'deposit'} mayCancel={t.mayCancel} />
            </li>
          ))}
        </ul>
      ) : null}
      {handing ? <HandOverModal custody={data} onClose={() => setHanding(false)} /> : null}
    </Card>
  );
}

function HandOverModal({ custody, onClose }: { custody: MoneyCustody; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'UTC');
  const record = useRecordTransfer();
  const [key] = useState(newSubmissionKey);
  const [fromAccountId, setFrom] = useState(custody.held[0]?.accountId ?? '');
  const [toAccountId, setTo] = useState(custody.destinations.find((d) => d.kind === 'cash')?.accountId ?? custody.destinations[0]?.accountId ?? '');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [secondOne, setSecondOne] = useState(false);
  const duplicate = record.error instanceof ApiError && record.error.status === 409 && Boolean(record.error.fields?.confirmDuplicate);
  const to = custody.destinations.find((d) => d.accountId === toAccountId);
  const held = custody.held.find((h) => h.accountId === fromAccountId);
  return (
    <Modal open onClose={onClose} title="Hand over cash">
      <form
        className="space-y-4"
        aria-label="Hand over cash"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          record.mutate(
            {
              fromAccountId, toAccountId, amount, transferredOn: today, idempotencyKey: key, pending: true,
              ...(reference.trim() ? { reference: reference.trim() } : {}),
              ...(secondOne ? { confirmDuplicate: true } : {}),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="From"
            value={fromAccountId}
            onChange={(e) => setFrom(e.target.value)}
            options={custody.held.map((h) => ({ value: h.accountId, label: `${h.accountName} (${money(h.balance, custody.currencyCode)})` }))}
          />
          <Select
            label="To"
            value={toAccountId}
            onChange={(e) => setTo(e.target.value)}
            options={custody.destinations.map((d) => ({
              value: d.accountId,
              label: d.kind === 'cash' ? `${d.holderName ?? d.accountName} (${d.accountName})` : `${d.accountName} — bank`,
            }))}
          />
          <Input label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} hint={held ? `You hold ${money(held.balance, custody.currencyCode)}` : undefined} />
          <Input label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder={to?.kind === 'cash' ? 'Optional' : 'Deposit slip number'} />
        </div>
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {to?.kind === 'cash'
            ? `It leaves you now and is ${to.holderName ?? 'the receiver'}'s once they confirm they received it.`
            : 'It leaves you now and reaches the bank once the accountant or owner confirms it under the bank’s reference.'}
        </p>
        {duplicate ? (
          <label className="flex items-start gap-2 text-sm text-amber-900">
            <input type="checkbox" className="mt-1" checked={secondOne} onChange={(e) => setSecondOne(e.target.checked)} />
            <span>This is a second, separate amount — not the same cash entered again.</span>
          </label>
        ) : null}
        {record.error ? <p role="alert" className="text-sm text-red-700">{record.error.message}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={record.isPending} disabled={!amount || !fromAccountId || !toAccountId || (duplicate && !secondOne)}>
            Hand over
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** For a worker who hands over cash but does not read the business's figures. */
export function MyCashPage(): ReactNode {
  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Cash &amp; bank</h1>
        <p className="mt-0.5 text-sm text-slate-500">The cash you hold, and handing it over.</p>
      </header>
      <MyCash standalone />
    </div>
  );
}

